import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, User, Sparkles, Wallet, Copy, Share2, Crown, LogOut, ChevronRight, Link2, MessageCircle, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";
import { Avatar, Pill, SectionLabel } from "@/components/jk/kit";

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const ManageItem = ({ icon, label, sub, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex w-full items-center gap-3 rounded-[14px] px-3 py-3 text-left transition-colors hover:bg-[#f2f0ec] active:scale-[0.99]">
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f2f0ec] text-[#111111]">{icon}</span>
    <span className="min-w-0 flex-1">
      <span className="block text-[14px] font-medium text-[#111111]">{label}</span>
      {sub && <span className="block truncate text-[12px] text-[#888]">{sub}</span>}
    </span>
    <ChevronRight size={18} className="text-[#cfccc6]" />
  </button>
);

export const AccountDrawer = () => {
  const navigate = useNavigate();
  const { astrologer, logout } = useAuth();
  const { isOpen, close } = useMenu();
  const [copied, setCopied] = useState(false);

  if (!astrologer) return null;

  const slug = astrologer?.booking_slug || "";
  const link = `${window.location.origin}/${slug}`;
  const isPro = astrologer?.subscription_status === "active";

  const go = (path) => { close(); navigate(path); };

  const copy = async () => {
    const ok = await copyToClipboard(link);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Booking link copied");
    } else {
      toast.error("Couldn't copy — long-press the link to copy it.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Book a consultation",
          text: `Book a consultation with ${astrologer?.full_name || "me"} on JothidaKural`,
          url: link,
        });
      } catch (e) {
        if (e?.name !== "AbortError") copy();
      }
    } else {
      copy();
    }
  };

  const signOut = () => { close(); logout(); navigate("/signin", { replace: true }); };

  const shareWhatsApp = () => {
    const text = `Book a consultation with ${astrologer?.full_name || "me"} on JothidaKural ✨\n\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`fixed inset-0 z-50 ${isOpen ? "" : "pointer-events-none"}`} aria-hidden={!isOpen}>
      {/* Overlay */}
      <div
        onClick={close}
        data-testid="drawer-overlay"
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
      />
      {/* Panel */}
      <div
        data-testid="account-drawer"
        className={`absolute right-0 top-0 flex h-full w-[86%] max-w-[380px] flex-col bg-[var(--jk-paper)] shadow-2xl transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3 pt-6">
          <h2 className="text-[18px] font-bold tracking-[-0.01em] text-[#111111]">Menu</h2>
          <button onClick={close} data-testid="drawer-close" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f2f0ec] text-[#666666] active:scale-95">
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-8">
          {/* Identity */}
          <div className="flex items-center gap-3 rounded-[16px] border border-[#e9e6e1] bg-white p-3.5">
            <Avatar name={astrologer?.full_name} photo={astrologer?.photo_url} size={48} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold text-[#111111]">{astrologer?.full_name || "Your name"}</p>
              <p className="truncate text-[12px] text-[#666666]">{astrologer?.email}</p>
            </div>
          </div>

          {/* Manage */}
          <SectionLabel className="mb-1 mt-6 px-1">Manage</SectionLabel>
          <div className="rounded-[16px] border border-[#e9e6e1] bg-white p-1.5">
            <ManageItem testid="drawer-profile" icon={<User size={16} strokeWidth={1.9} />} label="Profile" sub="Photo, name, specialisations" onClick={() => go("/edit/profile")} />
            <ManageItem testid="drawer-services" icon={<Sparkles size={16} strokeWidth={1.9} />} label="Services" sub="Consultations & fees" onClick={() => go("/edit/services")} />
            <ManageItem testid="drawer-payout" icon={<Wallet size={16} strokeWidth={1.9} />} label="Get Paid" sub="Payout details" onClick={() => go("/edit/payout")} />
            <ManageItem testid="drawer-preview" icon={<Eye size={16} strokeWidth={1.9} />} label="Preview my page" sub="See your public booking page" onClick={() => slug && go(`/${slug}?preview=1`)} />
          </div>

          {/* Booking link */}
          <div className="mt-6 rounded-[16px] bg-[#111111] p-5 text-white">
            <SectionLabel className="text-white/60" icon={<Link2 size={13} strokeWidth={2} />}>Your booking link</SectionLabel>
            <p className="mt-2 break-all text-[14px] font-semibold" data-testid="drawer-link">{link.replace(/^https?:\/\//, "")}</p>
            <div className="mt-4 flex gap-3">
              <button onClick={copy} data-testid="drawer-copy-link" className="flex flex-1 items-center justify-center gap-2 rounded-[12px] bg-white py-2.5 text-[13px] font-semibold text-[#111111]"><Copy size={15} /> {copied ? "Copied!" : "Copy"}</button>
              <button onClick={share} data-testid="drawer-share-link" className="flex flex-1 items-center justify-center gap-2 rounded-[12px] border border-white/30 py-2.5 text-[13px] font-semibold text-white"><Share2 size={15} /> Share</button>
            </div>
            <button onClick={shareWhatsApp} data-testid="drawer-whatsapp-link" className="mt-3 flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#25D366] py-2.5 text-[13px] font-bold text-white active:scale-[0.98]"><MessageCircle size={15} strokeWidth={2.25} /> Share on WhatsApp</button>
          </div>

          {/* Subscription */}
          <SectionLabel className="mb-1 mt-6 px-1">Subscription</SectionLabel>
          <div className="rounded-[16px] border border-[#e9e6e1] bg-white px-4">
            <div className="flex items-center gap-3 py-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--jk-butter)] text-[#111111]"><Crown size={16} strokeWidth={1.75} /></span>
              <span className="flex-1 text-[14px] font-medium text-[#111111]">Current plan</span>
              <Pill tone={isPro ? "dark" : "periwinkle"}>{isPro ? "Pro · ₹750/mo" : "Free plan"}</Pill>
            </div>
            {!isPro && (
              <div className="border-t border-[#f0ede8]">
                <ManageItem testid="drawer-upgrade" icon={<Crown size={16} strokeWidth={1.75} />} label="Upgrade to Pro" onClick={() => go("/paywall")} />
              </div>
            )}
          </div>

          {/* Log out */}
          <div className="mt-6 rounded-[16px] border border-[#e9e6e1] bg-white px-4">
            <button onClick={signOut} data-testid="drawer-logout" className="flex w-full items-center gap-3 py-3.5 text-left">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fdeceb] text-[#d23b2f]"><LogOut size={16} strokeWidth={1.75} /></span>
              <span className="flex-1 text-[14px] font-medium text-[#d23b2f]">Log out</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDrawer;
